import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Building2, Sparkles, Phone, ArrowRight, ChevronDown, UtensilsCrossed, Shirt, Flame, Refrigerator, Droplets, Blinds, Trash2, Fence } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
import { ServiceNav } from '../components/ServiceNav';
const cardClass = 'bg-white rounded-2xl border border-gray-200 p-8 md:p-10 shadow-sm';
const stepHeadingClass = 'font-bebas text-2xl md:text-3xl text-[#1a2744] pb-3 border-b border-gray-200';
const labelClass = 'text-[#1a2744] font-medium text-sm';
const inputClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all placeholder-gray-400';
const selectClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all appearance-none cursor-pointer';
type PillGroupProps = {
  options: string[];
  selected: string | string[];
  onSelect: (val: string) => void;
  multi?: boolean;
};
function PillGroup({
  options,
  selected,
  onSelect,
  multi = false
}: PillGroupProps) {
  const isSelected = (opt: string) => multi ? (selected as string[]).includes(opt) : selected === opt;
  return <div className="flex flex-wrap gap-3" data-id="element-1637">
      {options.map(opt => <button key={opt} type="button" onClick={() => onSelect(opt)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${isSelected(opt) ? 'bg-[#e91e8c] border-[#e91e8c] text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-[#e91e8c] hover:text-[#e91e8c]'}`} data-id="element-1638">
          {opt}
        </button>)}
    </div>;
}
const extras = [{
  name: 'Washing dishes',
  price: 7,
  icon: UtensilsCrossed
}, {
  name: 'Laundry (wash & fold)',
  price: 7,
  icon: Shirt
}, {
  name: 'Inside of oven',
  price: 7,
  icon: Flame
}, {
  name: 'Inside of fridge',
  price: 7,
  icon: Refrigerator
}, {
  name: 'Flu Buster - Eucalyptus',
  price: 7,
  icon: Droplets
}, {
  name: 'Wet Wipe Blinds',
  price: 7,
  icon: Blinds
}, {
  name: 'Property Cleanup',
  price: 7,
  icon: Trash2
}, {
  name: 'Patio or Balcony',
  price: 7,
  icon: Fence
}];
const timeSlots = ['14:00', '15:00'];
export function SpringDeepCleaningQuote() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Step 1 - Contact
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact({
      ...contact,
      [e.target.name]: e.target.value
    });
  };
  // Step 2 - Location
  const [location, setLocation] = useState({
    address: '',
    unit: '',
    city: '',
    postalCode: ''
  });
  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocation({
      ...location,
      [e.target.name]: e.target.value
    });
  };
  // Step 3 - Cleaning details
  const [hours, setHours] = useState('2');
  const [cleaners, setCleaners] = useState('1');
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [additionalRooms, setAdditionalRooms] = useState('');
  const [floors, setFloors] = useState('');
  const [parking, setParking] = useState('');
  const [pets, setPets] = useState<string[]>(['None']);
  // Step 4 - Extras
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const toggleExtra = (name: string) => {
    setSelectedExtras(prev => prev.includes(name) ? prev.filter(e => e !== name) : [...prev, name]);
  };
  // Step 5 - Frequency
  const [frequency, setFrequency] = useState('');
  // Step 6 - Date & Time
  const [date, setDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  const handlePetSelect = (pet: string) => {
    if (pet === 'None') {
      setPets(['None']);
    } else {
      setPets(prev => {
        const withoutNone = prev.filter(p => p !== 'None');
        if (withoutNone.includes(pet)) {
          const result = withoutNone.filter(p => p !== pet);
          return result.length === 0 ? ['None'] : result;
        }
        return [...withoutNone, pet];
      });
    }
  };
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-1639">
      <SEOHead title="Book Spring / Deep Cleaning | Smith & Jengs Cleaning Services" description="Book your deep or spring cleaning service in Bicester and Oxfordshire. Thorough top-to-bottom cleaning, online booking in 60 seconds." canonical="https://smithandjengs.com/spring-deep-cleaning-quote" data-id="element-1640" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'Spring / Deep Cleaning',
      url: 'https://smithandjengs.com/spring-deep-cleaning'
    }, {
      name: 'Book Online',
      url: 'https://smithandjengs.com/spring-deep-cleaning-quote'
    }]} data-id="element-1641" />
      <Navbar data-id="element-1642" />

      <main className="flex-1 pt-24" data-id="element-1643">
        <ServiceNav data-id="element-1644" />

        {/* Header */}
        <section className="w-full bg-white pt-12 md:pt-16 pb-12 px-4 md:px-12" data-id="element-1645">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="max-w-5xl w-full mx-auto text-center" data-id="element-1646">
            <h1 className="font-bebas text-[#1a2744] leading-[0.85] tracking-wide mb-6" data-id="element-1647">
              <span className="text-5xl md:text-6xl lg:text-7xl block" data-id="element-1648">
                BOOK ONLINE
              </span>
              <span className="text-3xl md:text-4xl lg:text-5xl block text-[#e91e8c]" data-id="element-1649">
                SPRING / DEEP CLEANING
              </span>
            </h1>
            <p className="text-gray-600 font-medium max-w-2xl mx-auto text-lg" data-id="element-1650">
              It only takes 60 seconds to book your deep home cleaning. Fill in
              the details below and we'll take care of the rest.
            </p>
          </motion.div>
        </section>

        {/* Form Section */}
        <section className="w-full bg-[#f8f9fa] py-12 md:py-16 px-4 md:px-8" id="booking-form" data-id="element-1651">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="max-w-4xl w-full mx-auto" data-id="element-1652">
            <form className="space-y-8" onSubmit={e => e.preventDefault()} data-id="element-1653">
              {/* STEP 1: Contact */}
              <div className={cardClass} data-id="element-1654">
                <h3 className={stepHeadingClass} data-id="element-1655">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1656">STEP 1:</span> Contact
                  Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1657">
                  <div className="space-y-2" data-id="element-1658">
                    <label className={labelClass} data-id="element-1659">First Name</label>
                    <input type="text" name="firstName" value={contact.firstName} onChange={handleContactChange} placeholder="First Name" className={inputClass} data-id="element-1660" />
                  </div>
                  <div className="space-y-2" data-id="element-1661">
                    <label className={labelClass} data-id="element-1662">Last Name</label>
                    <input type="text" name="lastName" value={contact.lastName} onChange={handleContactChange} placeholder="Last Name" className={inputClass} data-id="element-1663" />
                  </div>
                  <div className="space-y-2" data-id="element-1664">
                    <label className={labelClass} data-id="element-1665">Email</label>
                    <input type="email" name="email" value={contact.email} onChange={handleContactChange} placeholder="Email Address" className={inputClass} data-id="element-1666" />
                  </div>
                  <div className="space-y-2" data-id="element-1667">
                    <label className={labelClass} data-id="element-1668">Phone Number</label>
                    <input type="tel" name="phone" value={contact.phone} onChange={handleContactChange} placeholder="Phone Number" className={inputClass} data-id="element-1669" />
                  </div>
                </div>
              </div>

              {/* STEP 2: Location */}
              <div className={cardClass} data-id="element-1670">
                <h3 className={stepHeadingClass} data-id="element-1671">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1672">STEP 2:</span> Cleaning
                  Location
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1673">
                  <div className="space-y-2" data-id="element-1674">
                    <label className={labelClass} data-id="element-1675">Street Address</label>
                    <input type="text" name="address" value={location.address} onChange={handleLocationChange} placeholder="Enter a location" className={inputClass} data-id="element-1676" />
                  </div>
                  <div className="space-y-2" data-id="element-1677">
                    <label className={labelClass} data-id="element-1678">Unit/Apt/Suite #</label>
                    <input type="text" name="unit" value={location.unit} onChange={handleLocationChange} placeholder="Unit/Apt/Suite #" className={inputClass} data-id="element-1679" />
                  </div>
                  <div className="space-y-2" data-id="element-1680">
                    <label className={labelClass} data-id="element-1681">City</label>
                    <input type="text" name="city" value={location.city} onChange={handleLocationChange} placeholder="City" className={inputClass} data-id="element-1682" />
                  </div>
                  <div className="space-y-2" data-id="element-1683">
                    <label className={labelClass} data-id="element-1684">Postal Code</label>
                    <input type="text" name="postalCode" value={location.postalCode} onChange={handleLocationChange} placeholder="Postal Code" className={inputClass} data-id="element-1685" />
                  </div>
                </div>
              </div>

              {/* STEP 3: Cleaning Details */}
              <div className={cardClass} data-id="element-1686">
                <h3 className={stepHeadingClass} data-id="element-1687">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1688">STEP 3:</span> Tell Us
                  About the Cleaning
                </h3>
                <div className="space-y-6 mt-8" data-id="element-1689">
                  {/* Hours & Cleaners */}
                  <div data-id="element-1690">
                    <label className={`${labelClass} block mb-3`} data-id="element-1691">
                      How many hours and cleaners would you like to book?
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1692">
                      <div className="relative" data-id="element-1693">
                        <select value={hours} onChange={e => setHours(e.target.value)} className={selectClass} data-id="element-1694">
                          {[2, 3, 4, 5, 6, 7, 8].map(h => <option key={h} value={String(h)} data-id="element-1695">
                              {h} Hours
                            </option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1696" />
                      </div>
                      <div className="relative" data-id="element-1697">
                        <select value={cleaners} onChange={e => setCleaners(e.target.value)} className={selectClass} data-id="element-1698">
                          {[1, 2, 3, 4].map(c => <option key={c} value={String(c)} data-id="element-1699">
                              {c} Cleaner{c > 1 ? 's' : ''}
                            </option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1700" />
                      </div>
                    </div>
                  </div>

                  {/* Bedrooms */}
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-id="element-1701">
                    <label className={`${labelClass} md:flex-1`} data-id="element-1702">
                      Bedrooms
                    </label>
                    <div className="relative md:w-64" data-id="element-1703">
                      <select value={bedrooms} onChange={e => setBedrooms(e.target.value)} className={selectClass} data-id="element-1704">
                        <option value="" disabled data-id="element-1705">
                          Select
                        </option>
                        <option value="studio" data-id="element-1706">Studio / 1 Bedroom</option>
                        <option value="2" data-id="element-1707">2 Bedrooms</option>
                        <option value="3" data-id="element-1708">3 Bedrooms</option>
                        <option value="4" data-id="element-1709">4 Bedrooms</option>
                        <option value="5+" data-id="element-1710">5+ Bedrooms</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1711" />
                    </div>
                  </div>

                  {/* Bathrooms */}
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-id="element-1712">
                    <label className={`${labelClass} md:flex-1`} data-id="element-1713">
                      Bathrooms
                    </label>
                    <div className="relative md:w-64" data-id="element-1714">
                      <select value={bathrooms} onChange={e => setBathrooms(e.target.value)} className={selectClass} data-id="element-1715">
                        <option value="" disabled data-id="element-1716">
                          Select
                        </option>
                        <option value="1" data-id="element-1717">1 Bathroom</option>
                        <option value="2" data-id="element-1718">2 Bathrooms</option>
                        <option value="3" data-id="element-1719">3 Bathrooms</option>
                        <option value="4+" data-id="element-1720">4+ Bathrooms</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1721" />
                    </div>
                  </div>

                  {/* Additional Rooms */}
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-id="element-1722">
                    <label className={`${labelClass} md:flex-1`} data-id="element-1723">
                      Additional Rooms (dining room, living room, office, etc)
                    </label>
                    <div className="relative md:w-64" data-id="element-1724">
                      <select value={additionalRooms} onChange={e => setAdditionalRooms(e.target.value)} className={selectClass} data-id="element-1725">
                        <option value="" disabled data-id="element-1726">
                          Select
                        </option>
                        <option value="0" data-id="element-1727">No additional rooms</option>
                        <option value="1" data-id="element-1728">1</option>
                        <option value="2" data-id="element-1729">2</option>
                        <option value="3" data-id="element-1730">3</option>
                        <option value="4+" data-id="element-1731">4+</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1732" />
                    </div>
                  </div>

                  {/* Number of Floors */}
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-id="element-1733">
                    <label className={`${labelClass} md:flex-1`} data-id="element-1734">
                      Number of Floors (incl basement)
                    </label>
                    <div className="relative md:w-64" data-id="element-1735">
                      <select value={floors} onChange={e => setFloors(e.target.value)} className={selectClass} data-id="element-1736">
                        <option value="" disabled data-id="element-1737">
                          Select
                        </option>
                        <option value="1" data-id="element-1738">1 Floor</option>
                        <option value="2" data-id="element-1739">2 Floors</option>
                        <option value="3" data-id="element-1740">3 Floors</option>
                        <option value="4+" data-id="element-1741">4+ Floors</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1742" />
                    </div>
                  </div>

                  {/* Parking */}
                  <div className="space-y-3" data-id="element-1743">
                    <label className={labelClass} data-id="element-1744">Parking</label>
                    <PillGroup options={['Free on street', 'Free home parking', 'Parking meters', 'Paid parking lot']} selected={parking} onSelect={setParking} data-id="element-1745" />
                  </div>

                  {/* Pets */}
                  <div className="space-y-3" data-id="element-1746">
                    <label className={labelClass} data-id="element-1747">
                      Do you have any pets?{' '}
                      <span className="text-gray-400 font-normal" data-id="element-1748">
                        Required for safety & allergy reasons
                      </span>
                    </label>
                    <PillGroup options={['None', 'Dog', 'Cat']} selected={pets} onSelect={handlePetSelect} multi data-id="element-1749" />
                  </div>
                </div>
              </div>

              {/* STEP 4: Deep Cleaning Extras */}
              <div className={cardClass} data-id="element-1750">
                <h3 className={stepHeadingClass} data-id="element-1751">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1752">STEP 4:</span> Deep
                  Cleaning Extras
                </h3>
                <p className="text-sm text-gray-500 mt-3" data-id="element-1753">
                  Free up more of your precious time for what matters most.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-id="element-1754">
                  {extras.map(({
                  name,
                  price,
                  icon: IconComp
                }) => {
                  const isActive = selectedExtras.includes(name);
                  return <button key={name} type="button" onClick={() => toggleExtra(name)} className={`flex flex-col items-center text-center gap-3 p-5 rounded-xl border-2 transition-all ${isActive ? 'border-[#e91e8c] bg-[#e91e8c]/5 shadow-sm' : 'border-gray-200 bg-white hover:border-[#e91e8c]/40'}`} data-id="element-1755">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${isActive ? 'bg-[#e91e8c]/10 text-[#e91e8c]' : 'bg-[#f8f9fa] text-[#1a2744]'}`} data-id="element-1756">
                          <IconComp className="w-6 h-6" data-id="element-1757" />
                        </div>
                        <span className="text-xs font-medium text-[#1a2744] leading-tight" data-id="element-1758">
                          {name}
                        </span>
                        <span className={`text-xs font-semibold ${isActive ? 'text-[#e91e8c]' : 'text-gray-500'}`} data-id="element-1759">
                          £{price}
                        </span>
                      </button>;
                })}
                </div>
              </div>

              {/* STEP 5: Frequency */}
              <div className={cardClass} data-id="element-1760">
                <h3 className={stepHeadingClass} data-id="element-1761">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1762">STEP 5:</span> Select
                  Frequency
                </h3>
                <div className="mt-6" data-id="element-1763">
                  <PillGroup options={['One time', 'Quarterly']} selected={frequency} onSelect={setFrequency} data-id="element-1764" />
                </div>
              </div>

              {/* STEP 6: Date & Time */}
              <div className={cardClass} data-id="element-1765">
                <h3 className={stepHeadingClass} data-id="element-1766">
                  <span className="text-[#e91e8c] mr-2" data-id="element-1767">STEP 6:</span> Date of
                  Service
                </h3>
                <p className="text-sm text-gray-500 mt-3" data-id="element-1768">
                  For recurring services, each one will commence on the same day
                  and time as selected for the first service.
                </p>
                <div className="space-y-6 mt-8" data-id="element-1769">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1770">
                    <div className="space-y-2" data-id="element-1771">
                      <label className={labelClass} data-id="element-1772">Select a date</label>
                      <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputClass} data-id="element-1773" />
                    </div>
                    <div className="space-y-2" data-id="element-1774">
                      <label className={labelClass} data-id="element-1775">Select a time slot</label>
                      <div className="flex gap-3" data-id="element-1776">
                        {timeSlots.map(slot => <button key={slot} type="button" onClick={() => setTimeSlot(slot)} className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all border ${timeSlot === slot ? 'bg-[#e91e8c] border-[#e91e8c] text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-[#e91e8c] hover:text-[#e91e8c]'}`} data-id="element-1777">
                            {slot}
                          </button>)}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400" data-id="element-1778">
                    For same day bookings, a customer service agent will call
                    you.
                  </p>
                  <div className="space-y-2" data-id="element-1779">
                    <label className={labelClass} data-id="element-1780">
                      Are there any additional details we need to know?
                    </label>
                    <textarea value={additionalDetails} onChange={e => setAdditionalDetails(e.target.value)} placeholder="Any special instructions or details..." rows={4} className="w-full border border-gray-200 rounded-lg p-4 bg-white text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all resize-none placeholder-gray-400" data-id="element-1781" />
                  </div>
                </div>
              </div>

              {/* STEP 7: Submit */}
              <div className={cardClass} data-id="element-1782">
                <div className="text-center space-y-4" data-id="element-1783">
                  <h3 className="font-bebas text-2xl md:text-3xl text-[#1a2744]" data-id="element-1784">
                    Ready to Book?
                  </h3>
                  <p className="text-gray-500 text-sm max-w-md mx-auto" data-id="element-1785">
                    Review your details above and click below to confirm your
                    deep cleaning booking. We'll send you a confirmation email
                    right away.
                  </p>
                  <button type="submit" className="bg-[#e91e8c] hover:bg-[#d0187c] text-white px-12 py-4 rounded-full font-semibold tracking-wider text-lg transition-colors shadow-lg hover:shadow-xl" data-id="element-1786">
                    BOOK NOW
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </section>

        {/* Explore Our Services */}
        <section className="w-full bg-white py-20 px-4 md:px-12 border-t border-gray-200" data-id="element-1787">
          <div className="max-w-7xl mx-auto" data-id="element-1788">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-16" data-id="element-1789">
              <h2 className="font-bebas text-4xl md:text-5xl lg:text-6xl text-[#1a2744] tracking-wide" data-id="element-1790">
                EXPLORE OUR <span className="text-[#e91e8c]" data-id="element-1791">SERVICES</span>
              </h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto" data-id="element-1792">
                Discover our full range of professional cleaning solutions
                tailored to your needs.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1793">
              {[{
              title: 'Basic Home Cleaning',
              desc: 'Regular maintenance to keep your home spotless.',
              link: '/basic-home-cleaning',
              icon: Home
            }, {
              title: 'Spring / Deep Cleaning',
              desc: 'Thorough, top-to-bottom detail cleaning.',
              link: '/spring-deep-cleaning',
              icon: Sparkles
            }, {
              title: 'Commercial Cleaning',
              desc: 'Professional cleaning for your business.',
              link: '/commercial-cleaning',
              icon: Building2
            }, {
              title: 'Contact Us',
              desc: 'Get in touch for custom requests.',
              link: '/contact-us',
              icon: Phone
            }].map((service, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: idx * 0.1
            }} data-id="element-1794">
                  <Link to={service.link} className="group block h-full bg-[#f8f9fa] rounded-xl p-8 hover:shadow-md transition-all border border-gray-100 hover:border-[#5bbcd5]" data-id="element-1795">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-6 group-hover:bg-[#5bbcd5]/10 transition-colors" data-id="element-1796">
                      <service.icon className="w-6 h-6 text-[#1a2744] group-hover:text-[#5bbcd5] transition-colors" data-id="element-1797" />
                    </div>
                    <h3 className="font-bebas text-2xl text-[#1a2744] mb-3 tracking-wide group-hover:text-[#e91e8c] transition-colors" data-id="element-1798">
                      {service.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-6 line-clamp-2" data-id="element-1799">
                      {service.desc}
                    </p>
                    <div className="flex items-center text-[#5bbcd5] font-medium text-sm mt-auto" data-id="element-1800">
                      Learn more
                      <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-1801" />
                    </div>
                  </Link>
                </motion.div>)}
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1802" />
    </div>;
}